import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { intersection, keyBy, keys, without } from 'lodash';

import { WEBAPP_CB2_DEFAULT_PAGE_ROUTES, type WebappDefaultPageRoute } from '@front-platform/content-builder';

import { DEFAULT_FONTS } from '@config/fonts.config';
import { DEFAULT_COLORS } from '@config/colors.config';
import { CB2_DEFAULT_PAGES, DEFAULT_PAGES } from '@config/default-pages.config';
import { DEFAULT_BLOCKS } from '@config/default-blocks.config';
import { DEFAULT_MODALS } from '@config/default-modals.config';

import {
  DoodMarketplaceModel,
  IBlockConfig,
  IPageConfig,
  IUIConfigFont,
} from '@core/models/marketplace.model';
import { DistributionModeKeys, MarketplaceKeys } from '@config/keys/shop.keys';
import { DistributionModeValues, OrderTypeValues } from '@config/values/order.values';

import { OrderTypeService } from '@core/services/order-type/order-type.service';
import { ShopSearchParametersService } from '@core/services/shop-search-parameters/shop-search-parameters.service';

dayjs.extend(timezone);

/** Deps required to apply the default distribution mode. */
export interface InitializeDefaultDistributionModeDeps {
  shopSearchParametersService: ShopSearchParametersService;
  orderTypeService: OrderTypeService;
}

/**
 * Pure / document-only marketplace runtime helpers shared between the legacy
 * `MarketplaceService` boot pipeline and the CB2 preview applier. Extracted as
 * standalone functions so both callers run the exact same logic. Route
 * registration lives in `marketplace-routes.util.ts` to keep this module free
 * of the app-routing dependency tree.
 */

/** Flattens the marketplace `pages` list into a parent/children tree. */
export function convertToPageTree(marketplace: DoodMarketplaceModel): DoodMarketplaceModel {
  if (!marketplace.pages) {
    return marketplace;
  }

  const pages = marketplace.pages.map(page => ({ ...page, children: [] }));

  const pagesById = pages.reduce(
    (acc, page) => {
      acc[page.id ?? 'n'] = page;
      return acc;
    },
    {} as Record<string, IPageConfig>,
  );

  const rootPages = pages.filter(page => !page.parent);

  pages.forEach(page => {
    if (!page.parent) {
      return;
    }
    const parent = pagesById[page.parent];
    if (!parent) {
      return;
    }
    if (!parent.children) {
      parent.children = [];
    }
    parent.children.push(page);
  });

  return { ...marketplace, pages: rootPages };
}

/** Merges default pages not overridden by the marketplace, recursing on children. */
export function injectDefaultPages(
  pages?: IPageConfig[],
  defaults?: IPageConfig[],
  index = 0,
): IPageConfig[] {
  let result: IPageConfig[] = pages ?? [];

  if (pages) {
    const defaultPages = keyBy(defaults, 'path');
    const marketplacePages = keyBy(pages, 'path');

    const defaultPaths = keys({ ...defaultPages });
    const marketplacePaths = keys({ ...marketplacePages });

    const uniquePaths = without(defaultPaths, ...marketplacePaths);
    const allPaths = intersection(defaultPaths, [...marketplacePaths]);

    result = uniquePaths.reduce((acc, path) => {
      const page = defaultPages[path];
      return [...acc, page];
    }, result);

    const configs = Object.keys(marketplacePages).reduce((map, id) => {
      map.set(id, marketplacePages[id]);
      return map;
    }, new Map<string, IPageConfig>());

    allPaths.forEach(path => {
      const def = defaultPages[path];

      if (configs.has(path)) {
        let page = configs.get(path) as IPageConfig;

        if (!page.children) {
          page = { ...page, children: [] };
        }

        configs.set(path, {
          ...page,
          children: injectDefaultPages(page.children, def.children, index + 1),
        });
      }
    });

    result = result.map(route => {
      const config = configs.get(route.path);
      if (!config) return route;
      return { ...route, children: config.children };
    });
  }

  return result;
}

/** Prepends the default CB1 blocks to the marketplace blocks. */
export function injectDefaultBlocks(marketplace: DoodMarketplaceModel): DoodMarketplaceModel {
  const blocks = marketplace.blocks ?? [];
  return { ...marketplace, blocks: [...DEFAULT_BLOCKS, ...blocks] };
}

export const SYSTEM_MODAL_BLOCK_ID_PREFIX = 'dood-system-modal-';

function systemModalBlockId(handle: string): string {
  return `${SYSTEM_MODAL_BLOCK_ID_PREFIX}${handle}`;
}

/** Backing blocks for platform modals injected at boot (not in CB2 compiled runtime API). */
export function buildDefaultSystemModalBackingBlocks(): IBlockConfig[] {
  return DEFAULT_MODALS.map(modal => ({
    id: systemModalBlockId(modal.handle),
    type: modal.block.type,
    data: modal.block.data,
  }));
}

/** Keeps system modal blocks in the store when CB2 contextual resolution replaces blocks. */
export function mergeSystemModalBackingBlocks(resolvedBlocks: IBlockConfig[]): IBlockConfig[] {
  const resolvedIds = new Set(resolvedBlocks.map(block => block.id));
  const missing = buildDefaultSystemModalBackingBlocks().filter(
    block => !resolvedIds.has(block.id),
  );
  return missing.length ? [...resolvedBlocks, ...missing] : resolvedBlocks;
}

function collectBlockIdsFromDefaultRoutes(
  routes: readonly WebappDefaultPageRoute[],
  acc = new Set<string>(),
): Set<string> {
  for (const route of routes) {
    if (route.block) {
      acc.add(route.block);
    }
    if (route.children) {
      collectBlockIdsFromDefaultRoutes(route.children, acc);
    }
  }
  return acc;
}

/**
 * Page blocks injected for CB2 fallback routes, plus the slot blocks those
 * pages reference (account body, orders list, check details, …).
 */
export function buildCb2InjectedPageBackingBlocks(): IBlockConfig[] {
  const rootIds = collectBlockIdsFromDefaultRoutes(WEBAPP_CB2_DEFAULT_PAGE_ROUTES);
  const defaultsById = keyBy(DEFAULT_BLOCKS, 'id');
  return [...expandDefaultBlockIds(rootIds)]
    .map(id => defaultsById[id])
    .filter(block => block != null) as IBlockConfig[];
}

/** Follows string id lists on default blocks so a fallback page can render its slots. */
function expandDefaultBlockIds(rootIds: ReadonlySet<string>): Set<string> {
  const defaultsById = keyBy(DEFAULT_BLOCKS, 'id');
  const expanded = new Set<string>();
  const queue = [...rootIds];

  while (queue.length > 0) {
    const id = queue.pop();
    if (!id || expanded.has(id)) {
      continue;
    }
    const block = defaultsById[id];
    if (!block) {
      continue;
    }
    expanded.add(id);
    const data = block.data;
    if (!data || typeof data !== 'object') {
      continue;
    }
    for (const value of Object.values(data)) {
      if (!Array.isArray(value)) {
        continue;
      }
      for (const childId of value) {
        if (typeof childId === 'string' && !expanded.has(childId)) {
          queue.push(childId);
        }
      }
    }
  }

  return expanded;
}

/** Keeps CB2 fallback page blocks when contextual resolution replaces the block store. */
export function mergeCb2InjectedPageBackingBlocks(resolvedBlocks: IBlockConfig[]): IBlockConfig[] {
  const resolvedIds = new Set(resolvedBlocks.map(block => block.id));
  const missing = buildCb2InjectedPageBackingBlocks().filter(block => !resolvedIds.has(block.id));
  return missing.length ? [...resolvedBlocks, ...missing] : resolvedBlocks;
}

/** Re-injects boot-only blocks omitted from CB2 compiled contextual `setBlocks`. */
export function mergeContextualRuntimeBackingBlocks(resolvedBlocks: IBlockConfig[]): IBlockConfig[] {
  return mergeCb2InjectedPageBackingBlocks(mergeSystemModalBackingBlocks(resolvedBlocks));
}

function isModalBlockIdBacked(modal: { block?: unknown }, blocks: { id: string }[]): boolean {
  return typeof modal.block === 'string' && blocks.some(block => block.id === modal.block);
}

/** Merges default modals (and their backing blocks) not overridden by the marketplace. */
export function injectDefaultModals(marketplace: DoodMarketplaceModel): DoodMarketplaceModel {
  const blocks = [...(marketplace.blocks ?? [])];
  const modals = (marketplace.modals ?? []).filter(modal => isModalBlockIdBacked(modal, blocks));

  const defaultModalsByHandle = keyBy(DEFAULT_MODALS, 'handle');
  const marketplaceModalsByHandle = keyBy(modals, 'handle');
  const defaultModalsHandles = keys(defaultModalsByHandle);
  const marketplaceModalsHandles = keys(marketplaceModalsByHandle);
  const defaultModalsHandlesToLoad = without(defaultModalsHandles, ...marketplaceModalsHandles);

  defaultModalsHandlesToLoad.forEach(handle => {
    const modal = defaultModalsByHandle[handle];
    const blockId = systemModalBlockId(handle);
    if (!blocks.some(block => block.id === blockId)) {
      blocks.push({ id: blockId, type: modal.block.type, data: modal.block.data });
    }
    modals.push({ handle, block: blockId });
  });

  return { ...marketplace, modals, blocks };
}

/** Applies fonts + colors + custom CSS from the marketplace user interface. */
export function applyMarketplaceUserInterface(marketplace: DoodMarketplaceModel): void {
  loadFonts(marketplace);
  loadColors(marketplace);
  loadCustomCSS(marketplace);
}

function loadFonts(marketplace: DoodMarketplaceModel): void {
  const fonts = {
    ...DEFAULT_FONTS,
    ...marketplace.user_interface?.fonts,
    ...marketplace.user_interface?.fonts,
  };
  if (!fonts) {
    return;
  }
  for (const [key, font] of Object.entries(fonts)) {
    importFontFamily(font);
    assignStyleProperty('--ui-font-' + key + '-family', font.family);
    assignStyleProperty('--ui-font-' + key + '-weight', font.weight?.toString() ?? '400');
  }
}

function loadColors(marketplace: DoodMarketplaceModel): void {
  const colors = {
    ...DEFAULT_COLORS,
    ...marketplace.user_interface?.colors,
  };
  for (const [key, palette] of Object.entries(colors)) {
    if (!palette) {
      continue;
    }
    for (const [level, hex] of Object.entries(palette)) {
      assignStyleProperty('--ui-color-' + key + '-' + level, hex);
    }
  }
}

function loadCustomCSS(marketplace: DoodMarketplaceModel): void {
  const element = document.createElement('style');
  element.innerHTML = marketplace.user_interface?.custom_css?.join(' ') as string;
  document.head.appendChild(element);
}

function assignStyleProperty(key: string, value: string): void {
  document.documentElement.style.setProperty(key, value);
}

function importFontFamily({ family, url, local }: IUIConfigFont): void {
  if (!url) {
    return;
  }
  const style = document.createElement('style');
  if (!local) {
    style.appendChild(document.createTextNode(`@import url('${url}')`));
  } else {
    style.appendChild(
      document.createTextNode(
        `@font-face {
                font-family: '${family}';
                src: url('${url}');
            }`,
      ),
    );
  }
  document.querySelector('head')?.appendChild(style);
}

/** Sets the `<html lang>` attribute from the marketplace default locale. */
export function setMarketplaceHtmlLang(marketplace: DoodMarketplaceModel): void {
  document
    .querySelector('html')
    ?.setAttribute('lang', marketplace[MarketplaceKeys.DefaultLocale]?.locale ?? 'fr');
}

/** Sets the default dayjs timezone from the marketplace. */
export function setMarketplaceDefaultTimezone(marketplace: DoodMarketplaceModel): void {
  dayjs.tz.setDefault(marketplace[MarketplaceKeys.Timezone] ?? 'Europe/Paris');
}

/** Resolves the auth mode of the first enabled distribution mode, if any. */
export function resolveAuthMode(marketplace: DoodMarketplaceModel): string | undefined {
  return marketplace[MarketplaceKeys.DistributionModes].find(
    mode => mode[DistributionModeKeys.Enabled],
  )?.[DistributionModeKeys.AuthMode];
}

/** Applies the default distribution mode when no saved order type is active. */
export function initializeDefaultDistributionMode(
  marketplace: DoodMarketplaceModel,
  deps: InitializeDefaultDistributionModeDeps,
): void {
  const modes = [...marketplace.distribution_modes];
  const parameters = deps.shopSearchParametersService.getParameters();
  const savedOrderType = parameters.distribution_mode;
  const savedDistributionModeEnabled = marketplace.distribution_modes?.filter(
    mode =>
      mode.enabled &&
      deps.orderTypeService.convertOrderTypeToDistributionModes(savedOrderType).includes(mode.type),
  );

  if (savedOrderType && savedDistributionModeEnabled.length) {
    return;
  }

  const prioritySortedModes = [...modes].sort((a, b) => a.priority - b.priority);
  const enabledModes = prioritySortedModes.filter(m => m.enabled);

  let distributionMode: DistributionModeValues | OrderTypeValues | undefined;

  if (enabledModes.length) {
    distributionMode = enabledModes[0]?.type as DistributionModeValues;
  }

  switch (distributionMode) {
    case DistributionModeValues.Stuart:
    case DistributionModeValues.UberDirect:
    case DistributionModeValues.DeliverectDispatch:
    case DistributionModeValues.Shipday:
    case DistributionModeValues.ZitiCity:
    case DistributionModeValues.OwnDelivery:
      distributionMode = OrderTypeValues.Delivery;
      break;
  }
  if (!distributionMode) {
    return;
  }
  deps.shopSearchParametersService.setDistributionModeType(distributionMode, false);
}

function collectPageBlockIds(
  pages: readonly IPageConfig[] | undefined,
  acc = new Set<string>(),
): Set<string> {
  for (const page of pages ?? []) {
    if (typeof page.block === 'string' && page.block) {
      acc.add(page.block);
    }
    collectPageBlockIds(page.children, acc);
  }
  return acc;
}

/**
 * Merges `WEBAPP_CB2_DEFAULT_PAGE_ROUTES` with the same rules as v1.
 * A journey that already has Main Layout → cart keeps that cart page and gains
 * the product-add child route. The blocks those fallback pages reference are
 * added when missing. Cart item edit is a modal and is not injected here.
 */
export function injectCb2DefaultPages(marketplace: DoodMarketplaceModel): DoodMarketplaceModel {
  const pages = injectDefaultPages(marketplace.pages, CB2_DEFAULT_PAGES);
  const referencedBlockIds = expandDefaultBlockIds(collectPageBlockIds(pages));
  const blocks = [...(marketplace.blocks ?? [])];
  const blockIds = new Set(blocks.map(block => block.id));

  for (const block of DEFAULT_BLOCKS) {
    if (referencedBlockIds.has(block.id) && !blockIds.has(block.id)) {
      blocks.push(block);
      blockIds.add(block.id);
    }
  }

  return { ...marketplace, pages, blocks };
}

/** Injects default pages/blocks/modals into a marketplace (legacy boot order). */
export function injectDefaults(marketplace: DoodMarketplaceModel): DoodMarketplaceModel {
  const withPages = {
    ...marketplace,
    pages: injectDefaultPages(marketplace.pages, DEFAULT_PAGES),
  } as DoodMarketplaceModel;
  return injectDefaultModals(injectDefaultBlocks(withPages));
}
